import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import pathify, { make } from 'vuex-pathify'
import VuexPersistence from 'vuex-persist'
import router from '../router'
import { AZURE_SEARCH_DEFAULT_AXIOS_CONFIG } from '@/config/azure-search'
import schoolCalendars from '../data/school_calendars.json'

Vue.use(Vuex)

const apiEndpoint = process.env.VUE_APP_GRAPHQL_ENDPOINT

function getReferrer() {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search)
    const referrer = urlParams.get('referrer') || document.referrer
    if (
      referrer && referrer.includes('asd20.org')
    ) {
      return referrer.replace(/\/$/, '')
    }
  }
  return 'https://asd20.org'
}

const state = {
  // Display state
  view: 'calendar',
  referrer: getReferrer(),
  loading: 0,

  // Search results
  events: [],
  annualEvents: [],
  facets: null,
  suggestions: [],
  searchId: '',

  // Data
  calendarSubscriptions: [],
  organizations: [],
  calendarOptionsByOrganizationAndCategory: [],

  // Search filters
  keywords: '',
  year: new Date().getFullYear(),
  month: new Date().getMonth(),
  selectedOrganizationId: null,
  // includedOrganizationIds: ['26eaf390-d8ab-11e9-a3a8-5de5bba4f125'],
  includedOrganizationIds: [],
  selectedCalendarIds: [],
  selectedCategoriesByOrganizationId: {},

  // Search options
  enableFuzzySearch: true,

  // ID to exclude
  excludedOrganizationId: '7ab6f0b0-3304-11ef-92d6-ed16a99dd422', // Add the specific Organization ID here
}

const store = new Vuex.Store({
  plugins: [
    pathify.plugin,
    new VuexPersistence({
      storage: window.localStorage,
      reducer: state => ({
        view: state.view,
      }),
    }).plugin,
  ],

  state,

  mutations: {
    ...make.mutations(state), // spread in the pathify mutations

    SET_ANNUAL_EVENTS(state, events) {
      state.annualEvents = events
    },
  },

  actions: {
    ...make.actions(state),

    async setSelectedOrganizationId(
      { commit, dispatch, getters, state },
      selectedOrganizationId
    ) {
      commit('SET_SELECTED_ORGANIZATION_ID', selectedOrganizationId)
      if (!state.includedOrganizationIds.includes(selectedOrganizationId)) {
        dispatch('setIncludedOrganizationIds', [
          ...state.includedOrganizationIds,
          selectedOrganizationId,
        ])
      }
      const calendarIds = (state.calendarSubscriptions || [])
        .filter(s => s.ownerOrganizationId === selectedOrganizationId)
        .map(s => s.id)
      if (state.selectedCalendarIds.length === 0) {
        dispatch('clearFilters')
        dispatch('setSelectedCalendarIds', calendarIds)
      }
      dispatch('updateRoute')
    },

    async setSelectedCalendarIds({ commit, dispatch }, selectedCalendarIds) {
      commit('SET_SELECTED_CALENDAR_IDS', selectedCalendarIds)
      dispatch('updateRoute')
    },

    updateRoute({ getters, state }) {
      if (!getters.selectedOrganization) return

      const routeParams = {}

      const sanitizedAbbreviation = getters.selectedOrganization.abbreviation
        .toLowerCase()
        .replace('asd20', 'district')

      if (router.currentRoute.params.abbreviation !== sanitizedAbbreviation) {
        routeParams.abbreviation = sanitizedAbbreviation
      }

      router.replace({
        name: router.currentRoute.name,
        params: {
          ...router.currentRoute.params,
          ...routeParams,
        },
      })
    },
    setSelectedOrganizationByAbbreviation({ commit, getters }, abbreviation) {
      const match = state.organizations.find(
        org => org.abbreviation.toLowerCase() === abbreviation.toLowerCase()
      )
      if (match) {
        commit('SET_SELECTED_ORGANIZATION_ID', match.id)
      }
    },

    async getOrganizations({ commit, state }) {
      commit('SET_LOADING', state.loading + 1)
      const { data } = await axios.post(`${apiEndpoint}`, {
        query: `{
          organizations(order_by: { title: asc }, where: { id: { _neq: "${state.excludedOrganizationId}" } }) {
            id
            title
            abbreviation
            educationLevels
            primaryColor
            logoImageUrl
            website
            address
            phone
          }
        }`,
      })
      const organizations = (data && data.data && data.data.organizations) || []
      commit('SET_LOADING', state.loading - 1)

      commit('SET_ORGANIZATIONS', organizations)
    },

    async getCalendarSubscriptions({ commit, state }) {
      commit('SET_LOADING', state.loading + 1)

      const { data } = await axios.post(`${apiEndpoint}`, {
        query: `{
          calendarSubscriptions(order_by: { title: asc }) {
            id
            calendarId
            ownerOrganizationId
            ownerOrganization {
              title
              primaryColor
            }
            title
            categories
            keywords
            color
            format
            url
            createdDateTime
            modifiedDateTime
          }
        }`,
      })
      const calendarSubscriptions =
        (data && data.data && data.data.calendarSubscriptions) || []

      commit('SET_LOADING', state.loading - 1)
      commit('SET_CALENDAR_SUBSCRIPTIONS', calendarSubscriptions)
      return calendarSubscriptions
    },

    /**
     * Query the search API to collect all facets
     *
     * @param {*} { commit }
     * @returns
     */
    async getFacets({ commit, getters }) {
      // Query azure search
      let { data } = await axios({
        ...AZURE_SEARCH_DEFAULT_AXIOS_CONFIG,
        url: `${
          process.env.VUE_APP_AZURE_SEARCH_ENDPOINT
        }/indexes/events-index/docs/search`,
        data: {
          count: true,
          filter: `start ge ${getters.startDate.toISOString()} and start le ${getters.endDate.toISOString()}`,
          facets: [
            'calendarOrganizationTitle,count:1000',
            'calendarId,count:1000',
            'calendarCategories,count:1000',
          ],
          top: 1,
          select: 'id',
        },
      })

      // Update state
      if (data && data.value) {
        commit('SET_FACETS', data['@search.facets'])
      }
      return data.value
    },

    // Query azure search index for events
    async queryEvents({ commit, dispatch, state, getters }) {
      commit('SET_LOADING', state.loading + 1)
      let { data } = await axios({
        ...AZURE_SEARCH_DEFAULT_AXIOS_CONFIG,
        url: `${
          process.env.VUE_APP_AZURE_SEARCH_ENDPOINT
        }/indexes/events-index/docs/search`,
        data: getters.searchPayload,
      })
      commit('SET_LOADING', state.loading - 1)

      const events = (data && data.value) || []

      // Update state
      commit('SET_EVENTS', events || [])
      return events
    },

    async queryAnnualEvents({ commit, state }, year) {
      const start = new Date(year, 6, 1)
      const end = new Date(year + 1, 5, 30)

      const filters = []
      filters.push(`start ge ${start.toISOString()} and start le ${end.toISOString()}`)

      if (state.includedOrganizationIds.length > 0) {
        filters.push(
          `search.in(calendarOrganizationId, '${state.includedOrganizationIds.join(',')}', ',')`
        )
      }

      filters.push(
        `search.in(calendarId, '${state.selectedCalendarIds.join(',')}', ',')`
      )

      const payload = {
        count: true,
        search: '*',
        filter: filters.join(' and '),
        orderby: 'start asc',
        top: 1000,
      }

      const { data } = await axios({
        ...AZURE_SEARCH_DEFAULT_AXIOS_CONFIG,
        url: `${process.env.VUE_APP_AZURE_SEARCH_ENDPOINT}/indexes/events-index/docs/search`,
        data: payload,
      })

      const events = (data && data.value) || []

      // Commit annual events to state
      commit('SET_ANNUAL_EVENTS', events)
    },

    clearFilters({ dispatch }) {
      dispatch('setSelectedCalendarIds', [])
    },
  },

  getters: {
    selectedOrganization: state => {
      if (!state.selectedOrganizationId || !state.organizations.length) return null
      return state.organizations.find(
        o => o.id === state.selectedOrganizationId
      )
    },
    // selectedOrganization: state => {
    //   return state.organizations.find(
    //     o => o.id === state.selectedOrganizationId
    //   )
    // },
    organizationOptions: state => {
      return (state.organizations || []).map(o => ({
        text: o.title,
        value: o.id,
      }))
    },

    categories: state => {
      return state.calendarSubscriptions
        .map(calendarSubscription => calendarSubscription.categories)
        .reduce((a, categories) => {
          for (const category of categories) {
            if (!a.includes(category)) a.push(category)
          }
          return a
        }, [])
    },

    categoriesByOrganizationId: (state, getters) => {
      return state.organizations.reduce((a, organization) => {
        a[organization.id] = getters.categories.filter(category =>
          state.calendarSubscriptions.some(
            calendarSubscription =>
              calendarSubscription.ownerOrganizationId === organization.id &&
              calendarSubscription.categories.includes(category)
          )
        )
        return a
      }, {})
    },

    calendarOptionsByOrganizationAndCategory: (state, getters) => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      return (
        state.organizations
          .filter(org => state.includedOrganizationIds.includes(org.id))
          .map(organization => {
            const uncategorizedCalendarOptions = state.calendarSubscriptions
              .filter(cs => {
                return (
                  (!cs.categories || cs.categories.length === 0) &&
                  cs.ownerOrganization &&
                  cs.ownerOrganization.title === organization.title
                )
              })
              .map(cs => {
                const count = (state.events || []).filter(event => {
                  return (
                    event.calendarId === cs.id &&
                    new Date(event.start) >= today
                  )
                }).length

                return {
                  text: cs.title,
                  organizationTitle: cs.ownerOrganization.title,
                  value: cs.id,
                  count,
                  url: cs.url,
                  color: cs.color,
                }
              })
              .sort((a, b) => (a.text > b.text ? 1 : -1))

            const categorizedCalendarCategories = state.calendarSubscriptions
              .reduce((a, cs) => {
                for (const category of cs.categories || []) {
                  if (!a.includes(category)) a.push(category)
                }
                return a
              }, [])
              .reduce((a, category) => {
                const relatedSubscriptions = state.calendarSubscriptions.filter(
                  cs =>
                    cs.categories &&
                    cs.categories.includes(category) &&
                    cs.ownerOrganization.title === organization.title &&
                    cs.title !== category
                )

                const calendarOptions = relatedSubscriptions
                  .map(cs => {
                    const count = (state.events || []).filter(event => {
                      return (
                        event.calendarId === cs.id &&
                        new Date(event.start) >= today
                      )
                    }).length

                    return {
                      text: cs.title,
                      organizationTitle: cs.ownerOrganization.title,
                      value: cs.id,
                      count,
                      url: cs.url,
                      color: cs.color,
                    }
                  })
                  .sort((a, b) => (a.text > b.text ? 1 : -1))

                const selectedCount = calendarOptions.filter(o =>
                  state.selectedCalendarIds.includes(o.value)
                ).length

                if (calendarOptions.length > 0) {
                  a.push({
                    text: category,
                    value: category,
                    count: calendarOptions.reduce((sum, c) => sum + c.count, 0),
                    description:
                      selectedCount > 0 ? `${selectedCount} selected` : '',
                    calendarOptions,
                  })
                }

                return a
              }, [])
              .sort((a, b) => (a.text > b.text ? 1 : -1))

            const totalEvents =
              uncategorizedCalendarOptions.reduce((a, c) => a + c.count, 0) +
              categorizedCalendarCategories.reduce((a, c) => a + c.count, 0)

            return {
              organization: {
                ...organization,
                calendarLink: schoolCalendars.find(
                  c => c.id === organization.id
                ),
              },
              uncategorizedCalendarOptions,
              categorizedCalendarCategories,
              totalEvents,
            }
          })
          .sort((a, b) => {
            if (a.organization.id === state.selectedOrganizationId) return -1
            return a.organization.title > b.organization.title ? 1 : -1
          })
      )
    },

    startDate: state => {
      // Start from the first day of the month
      let d = new Date(state.year, state.month, 1)
      if (state.view === 'calendar') {
        // Subtract days, until we reach the first closest Sunday (for weekly view purposes)
        d.setDate(d.getDate() - d.getDay())
      }
      return d
    },

    endDate: (state, getters) => {
      // Start from last day of the month
      let d = new Date(state.year, state.month + 1, 1)
      // if (state.view === 'calendar') {
      //   // Add days from next month, so that we end on a Saturday
      //   let nextMonthDays = getters.startDate.getDay() * -1 + (6 - d.getDay())
      //   d.setDate(d.getDate() + nextMonthDays)
      // }
      return d
    },

    searchPayload: (state, getters) => {
      let query = '*'

      if (state.enableFuzzySearch) {
        // Search both by exact phrase and by fuzzy search of event name
        query = state.keywords.trim()
          ? `"${state.keywords.trim()}" OR summary:${state.keywords
              .trim()
              .split(' ')
              .join('* OR summary:')}*`
          : '*'
      } else {
        query = state.keywords.trim()
      }

      let filters = []

      let start = getters.startDate
      let end = getters.endDate
      filters.push(
        `start ge ${start.toISOString()} and start le ${end.toISOString()}`
      )

      if (state.includedOrganizationIds.length > 0) {
        filters.push(
          `search.in(calendarOrganizationId, '${state.includedOrganizationIds.join(
            ','
          )}', ',')`
        )
      }

      filters.push(
        `search.in(calendarId, '${state.selectedCalendarIds.join(', ')}')`
      )

      let payload = {
        count: true,
        queryType: state.enableFuzzySearch ? 'full' : 'simple',
        search: query,
        filter: filters.join(' and '),
        facets: ['calendarId,count:1000', 'calendarCategories,count:1000'],
        orderby: 'start asc',
        top: 1000,
      }

      return payload
    },

    totalFiltersApplied: (state, getters, rootState, rootGetters) => {
      return state.selectedCalendarIds.length
    },
  },

  strict: process.env.NODE_ENV !== 'production',
})

export default store
